import { useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { useSnackbar } from 'hooks/useSnackbar';
import i18n from 'i18n';
import { guestPortalIdSelector } from 'store/app/selectors';
import { updateRelocationsThunk } from 'store/itinerariesItem/relocations/thunks';
import { fetchGuestPortalThunk } from 'store/itinerariesItem/thunks';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { accountIdSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';
import axiosApi from 'utils/axios';
import { debounce } from 'utils/debounce';
import logger from 'utils/logger';

export function useActions() {
  const dispatch = useDispatch();
  const guestPortalId = useSelector(guestPortalIdSelector);
  const onSnackbar = useSnackbar();
  const accId = useSelector(accountIdSelector);
  const appHasChanges = useSelector(appHasChangesSelector);
  const [isSendingNotify, setIsSendingNotify] = useState(false);

  const createRelocation = async (relocation) => {
    try {
      await axiosApi.post(`/acc/${accId}/guest_portals/${guestPortalId}/relocations`, relocation);
      await dispatch(fetchGuestPortalThunk(guestPortalId));
      onSnackbar('The event has been created');
    } catch (err) {
      onSnackbar("Couldn't create.", true);
      throw err;
    }
  };
  const updateRelocation = async (relocation) => {
    try {
      await axiosApi.patch(`/acc/${accId}/guest_portals/${guestPortalId}/relocations/${relocation.id}`, relocation);
    } catch (err) {
      onSnackbar("Couldn't update.", true);
      throw err;
    } finally {
      dispatch(setAppHasChangesAction(false));
    }
  };

  const updateRelocationDebounced = debounce(async (relocation) => await updateRelocation(relocation), 2000);

  const deleteRelocation = async (id) => {
    try {
      await axiosApi.delete(`/acc/${accId}/guest_portals/${guestPortalId}/relocations/${id}`);
      await dispatch(fetchGuestPortalThunk(guestPortalId));
      onSnackbar('The event has been deleted');
    } catch (err) {
      logger.error(err);
      onSnackbar("Couldn't delete.", true);
    }
  };

  const handleChangeRelocation = async (itemIndex, relocation) => {
    dispatch(setAppHasChangesAction(true));
    dispatch(updateRelocationsThunk(itemIndex, relocation));
    await updateRelocationDebounced(relocation);
  };

  const handleEditRelocation = async (relocation) => {
    await updateRelocation(relocation);
    await dispatch(fetchGuestPortalThunk(guestPortalId));
  };

  const handleBlurRelocation = async (itemIndex, relocation) => await handleChangeRelocation(itemIndex, relocation);

  const showSuccessSubmitMessage = () => {
    confirmAlert({
      confirmButtonText: i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.popup_success.confirm'),
      onConfirm: () => null,
      title: i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.popup_success.body_agent'),
      showCancel: false,
    });
  };

  const handleSendNotify = () => {
    confirmAlert({
      confirmButtonText: i18n.t(
        'proposal_content.guest_portal.flight_and_transfers_page.notify_consultant.button.modal.options.send',
      ),
      cancelButtonText: i18n.t('common.buttons.cancel'),
      onConfirm: async () => {
        try {
          setIsSendingNotify(true);
          await axiosApi.put(`/acc/${accId}/guest_portals/${guestPortalId}/notify_agent_about_relocations_changes`);
          setIsSendingNotify(false);
          showSuccessSubmitMessage();
        } catch (err) {
          setIsSendingNotify(false);
          logger.error(err);
          onSnackbar('Something went wrong. Please try again later.', true);
        }
      },
      title: (
        <>
          {i18n.t('proposal_content.guest_portal.flight_and_transfers_page.notify_consultant.button.modal.description')}
        </>
      ),
    });
  };

  return {
    createRelocation,
    handleChangeRelocation,
    deleteRelocation,
    handleSendNotify,
    isSendingNotify,
    appHasChanges,
    handleBlurRelocation,
    handleEditRelocation,
  };
}
